import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { LOGO_WEBSITE_URL, SERVICES, CONTACT } from "@/lib/constants";
import { Menu, X, MessageCircle, Send } from "lucide-react";
import { XIcon, InstagramIcon } from "@/components/SocialIcons";

/* @section: navbar */
export default function Navbar() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  const links = [
    { href: "/", label: "主頁" },
    ...SERVICES.map((s) => ({ href: s.slug, label: s.name })),
    { href: "/blog", label: "博客" },
    { href: "/reviews", label: "客戶評價" },
  ];

  const scrollToAbout = (e: React.MouseEvent) => {
    e.preventDefault();
    const el = document.getElementById("about");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    } else {
      window.location.href = "/";
      setTimeout(() => document.getElementById("about")?.scrollIntoView({ behavior: "smooth" }), 500);
    }
    setOpen(false);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#2E8B57] border-b border-[#FFE000]/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0">
            <img
              src={LOGO_WEBSITE_URL}
              alt="YoYo Massage Wellness"
              className="h-10 w-auto rounded"
            />
          </Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-1">
            {links.map((l) => (
              <Link
                key={l.href}
                to={l.href}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                  loc.pathname === l.href
                    ? "bg-[#FFE000] text-[#2E8B57]"
                    : "text-gray-300 hover:text-[#FFE000] hover:bg-white/5"
                }`}
              >
                {l.label}
              </Link>
            ))}
            <a href="#about" onClick={scrollToAbout}
              className="px-3 py-1.5 rounded text-xs font-medium transition-colors text-gray-300 hover:text-[#FFE000] hover:bg-white/5">
              關於我們
            </a>
          </div>

          {/* Contact buttons */}
          <div className="hidden md:flex items-center gap-2">
            <a
              href={CONTACT.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#25D366] text-white text-xs font-medium rounded-full hover:bg-[#1ebe5b] transition-colors"
            >
              <MessageCircle size={14} />
              <span>WhatsApp</span>
            </a>
            <a
              href={CONTACT.telegram}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#2AABEE] text-white text-xs font-medium rounded-full hover:bg-[#1a9fd4] transition-colors"
            >
              <Send size={14} />
              <span>Telegram</span>
            </a>
            {/* X icon */}
            <a
              href={(CONTACT as any).twitter || "https://x.com/vivi80275642?s=11"}
              target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)"
              className="flex items-center justify-center w-8 h-8 rounded-full text-white hover:opacity-80 transition-opacity"
              style={{ background: "#000000" }}
            >
              <XIcon size={15} />
            </a>
            {/* Instagram icon */}
            <a
              href={(CONTACT as any).instagram || "https://www.instagram.com/yoyo_massage_wellness"}
              target="_blank" rel="noopener noreferrer" aria-label="Instagram"
              className="flex items-center justify-center w-8 h-8 rounded-full text-white hover:opacity-80 transition-opacity"
              style={{ background: "linear-gradient(45deg, #F58529, #DD2A7B, #8134AF)" }}
            >
              <InstagramIcon size={15} />
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            className="lg:hidden text-white p-2"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-[#2E8B57] border-t border-[#FFE000]/20 px-4 py-3 space-y-1">
          {links.map((l) => (
            <Link
              key={l.href}
              to={l.href}
              onClick={() => setOpen(false)}
              className={`block px-3 py-2 rounded text-sm font-medium transition-colors ${
                loc.pathname === l.href
                  ? "bg-[#FFE000] text-[#2E8B57]"
                  : "text-gray-300 hover:text-[#FFE000]"
              }`}
            >
              {l.label}
            </Link>
          ))}
          <a href="#about" onClick={scrollToAbout}
            className="block px-3 py-2 rounded text-sm font-medium text-gray-300 hover:text-[#FFE000] transition-colors">
            關於我們
          </a>
          <div className="flex gap-2 pt-2">
            <a
              href={CONTACT.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-[#25D366] text-white text-sm font-medium rounded-full"
            >
              <MessageCircle size={15} /> WhatsApp
            </a>
            <a
              href={CONTACT.telegram}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-[#2AABEE] text-white text-sm font-medium rounded-full"
            >
              <Send size={15} /> Telegram
            </a>
          </div>
          {/* X and IG in mobile menu */}
          <div className="flex gap-2 pt-1 pb-1">
            <a
              href={(CONTACT as any).twitter || "https://x.com/vivi80275642?s=11"}
              target="_blank" rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-1.5 py-2 text-white text-sm font-medium rounded-full"
              style={{ background: "#000000" }}
            >
              <XIcon size={15} /> X (Twitter)
            </a>
            <a
              href={(CONTACT as any).instagram || "https://www.instagram.com/yoyo_massage_wellness"}
              target="_blank" rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-1.5 py-2 text-white text-sm font-medium rounded-full"
              style={{ background: "linear-gradient(45deg, #F58529, #DD2A7B, #8134AF)" }}
            >
              <InstagramIcon size={15} /> Instagram
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}
